import { useUpdatePermissions } from "api/permissions/usePermissions";
import GuiContext from "config/context/guiContext";
import TableContext from "config/context/tableContext";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

const initialServerStatus = {
    response: null,
    loading: false,
    error: null,
}

export const useCreateComposition = ({ privateKey, tables, structs }) => {
    const navigate = useNavigate();
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();
    const { updatePermissions } = useUpdatePermissions();
    const { setActiveComposition } = useContext(TableContext);
    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const setComposition = async () => {

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/add_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    private_key: privateKey,
                    server_url: selectedServer || "",
                    tables: tables,
                    structs: structs,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();
            updatePermissions();
            setActiveComposition(data?.created);
            setTimeout(() => {
                navigate("/compositions");
            }, 1600);

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        setComposition,
        resetServerStatus
    };
};

export const useDropComposition = ({ privateKey, compositionName }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();
    const { tableStructures, setActiveComposition } = useContext(TableContext);

    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const dropComposition = async () => {
        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/drop_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount,
                    composition_name: compositionName,
                    private_key: privateKey,
                    server_url: selectedServer,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();
            setActiveComposition(tableStructures.find(item => item?.name !== compositionName)?.name || "");

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        dropComposition,
        resetServerStatus
    };
};

export const useClearComposition = ({ privateKey, compositionName, permissionName }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { activeComposition, setActiveComposition } = useContext(TableContext);
    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const clearComposition = async () => {
        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/clear_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount,
                    composition_name: compositionName,
                    private_key: privateKey,
                    server_url: selectedServer,
                    permission: permissionName,
                }),
            });

            const data = await res.json();
            setResponse(data);
            // updateDatabaseStructure();
            setActiveComposition(activeComposition);

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        clearComposition,
        resetServerStatus
    };
};

export const useSaveComposition = ({ privateKey, permission, actions, encryption }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();

    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const setComposition = async () => {

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/push_transaction`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount,
                    permission: permission,
                    private_key: privateKey,
                    actions: actions,
                    encrypted: encryption,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        setComposition,
        resetServerStatus
    };
};

export const useBackupDatabase = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const createBackup = async (options = {}) => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${URL}/api/plugins/backup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    database: selectedAccount || "",
                    server_url: selectedServer || "",
                    options: {
                        full_backup: options.full_backup ?? true,
                        compositions: options.compositions || [],
                        remote: options.remote || false,
                        method: options.method,
                        host: options.host,
                        user: options.user,
                        password: options.password,
                        path: options.path
                    }
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message || data?.error || 'Backup failed');
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        createBackup,
        resetServerStatus
    };
};

export const useUpdateDatabaseStructure = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setDatabaseStructure, setDatabaseStructureLoading, setActiveComposition } = useContext(TableContext);
    const { getCompositionsObjectStructure } = useGetCompositionsObjectStructure();

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const updateDatabaseStructure = async () => {
        setLoading(true);
        setDatabaseStructureLoading(true);
        try {
            const res = await fetch(`${URL}/api/database/get_database_structure`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    server_url: selectedServer || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            setDatabaseStructure(data);
            getCompositionsObjectStructure();
            setActiveComposition(data?.tables[0]?.name);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
            setDatabaseStructureLoading(false);
        }
    }

    return {
        response,
        loading,
        error,
        updateDatabaseStructure
    }
}

export const useGetCompositionsObjectStructure = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setCompositionsObjectStructure } = useContext(TableContext);

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const getCompositionsObjectStructure = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${URL}/api/database/get_compositions_object_structure`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    server_url: selectedServer || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            setCompositionsObjectStructure(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }

    return {
        response,
        loading,
        error,
        getCompositionsObjectStructure
    }
}